import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Check, ExternalLink, Copy, Bot } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  {
    title: "Créer une application Discord",
    content: "Allez sur le portail développeur Discord pour créer votre application bot.",
    instructions: [
      "Rendez-vous sur discord.com/developers/applications",
      "Cliquez sur \"New Application\" en haut à droite",
      "Donnez un nom à votre bot (ex: Mon Bot Gestion)",
      "Acceptez les conditions et cliquez sur \"Create\""
    ],
    link: "https://discord.com/developers/applications",
    linkLabel: "Ouvrir le portail développeur"
  },
  {
    title: "Configurer le Bot",
    content: "Activez la section Bot de votre application pour obtenir un token.",
    instructions: [
      "Dans le menu à gauche, cliquez sur \"Bot\"",
      "Cliquez sur \"Add Bot\" puis confirmez",
      "Sous \"USERNAME\", vous pouvez changer le nom du bot",
      "Vous pouvez aussi changer l'avatar du bot ici"
    ],
  },
  {
    title: "Récupérer le Token",
    content: "Le token est la clé secrète qui permet à votre bot de se connecter à Discord.",
    instructions: [
      "Dans la section Bot, cliquez sur \"Reset Token\"",
      "Confirmez en cliquant \"Yes, do it!\"",
      "Copiez le token qui apparaît (il ne sera plus visible après)",
      "IMPORTANT : Ne partagez jamais votre token avec personne !"
    ],
    warning: "Le token est comme un mot de passe. Si quelqu'un l'a, il contrôle votre bot."
  },
  {
    title: "Activer les Intents",
    content: "Les intents permettent au bot de recevoir certains événements Discord.",
    instructions: [
      "Toujours dans la section Bot, scrollez vers le bas",
      "Activez \"Presence Intent\"",
      "Activez \"Server Members Intent\"",
      "Activez \"Message Content Intent\"",
      "Cliquez sur \"Save Changes\""
    ],
  },
  {
    title: "Inviter le Bot sur votre serveur",
    content: "Générez un lien d'invitation pour ajouter le bot à votre serveur Discord.",
    instructions: [
      "Dans le menu à gauche, cliquez sur \"OAuth2\" > \"URL Generator\"",
      "Dans \"Scopes\", cochez \"bot\" et \"applications.commands\"",
      "Dans \"Bot Permissions\", cochez \"Administrator\" (ou les permissions souhaitées)",
      "Copiez l'URL générée en bas et ouvrez-la dans votre navigateur",
      "Sélectionnez votre serveur et cliquez \"Autoriser\""
    ],
  },
  {
    title: "Acheter sur Vanta Pulse",
    content: "Dernière étape : achetez votre bot sur notre plateforme pour l'activer.",
    instructions: [
      "Cliquez sur le menu ☰ puis \"Tarifs\"",
      "Choisissez votre bot (Gestion ou Radio) et un plan",
      "Au checkout, collez votre Token Discord (celui copié à l'étape 3)",
      "Donnez un nom à votre bot et complétez le paiement",
      "Votre bot sera activé automatiquement !"
    ],
    success: true
  },
];

export default function TutorialPage() {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <main className="pt-24 pb-16 max-w-2xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-8">
          <Bot className="w-8 h-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold">Créer son bot Discord</h1>
            <p className="text-white/40 text-sm">Guide étape par étape</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/5">
              <div
                className={`h-full rounded-full transition-all duration-300 ${i < step ? 'bg-green-500 w-full' : i === step ? 'bg-blue-500 w-full' : 'w-0'}`}
              />
            </div>
          ))}
        </div>

        {/* Step counter */}
        <p className="text-xs text-white/30 uppercase tracking-wider mb-4">Étape {step + 1} / {STEPS.length}</p>

        {/* Card */}
        <div className="bg-[#12121a] border border-white/5 rounded-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-bold mb-2">{current.title}</h2>
            <p className="text-white/50 text-sm mb-6">{current.content}</p>

            <div className="space-y-3">
              {current.instructions.map((inst, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs text-blue-400 font-bold">{i + 1}</span>
                  </div>
                  <p className="text-sm text-white/70">{inst}</p>
                </div>
              ))}
            </div>

            {current.warning && (
              <div className="mt-5 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-xs">{current.warning}</p>
              </div>
            )}

            {current.success && (
              <div className="mt-5 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-green-400 text-xs">Félicitations ! Votre bot sera opérationnel en quelques secondes après l'achat.</p>
              </div>
            )}

            {current.link && (
              <a href={current.link} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors">
                <ExternalLink className="w-4 h-4" />{current.linkLabel}
              </a>
            )}
          </div>

          {/* Navigation */}
          <div className="border-t border-white/5 p-4 flex items-center justify-between">
            <Button
              onClick={() => setStep(step - 1)}
              disabled={isFirst}
              variant="ghost"
              className="text-white/50 rounded-lg disabled:opacity-20"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />Précédent
            </Button>

            <div className="flex items-center gap-1.5">
              {STEPS.map((_, i) => (
                <button key={i} onClick={() => setStep(i)} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-blue-500' : i < step ? 'bg-green-500' : 'bg-white/10'}`} />
              ))}
            </div>

            {isLast ? (
              <Button onClick={() => window.location.href = '/'} className="bg-green-600 hover:bg-green-700 rounded-lg">
                <Check className="w-4 h-4 mr-1" />Terminé
              </Button>
            ) : (
              <Button onClick={() => setStep(step + 1)} className="bg-blue-600 hover:bg-blue-700 rounded-lg">
                Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
